import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { useScheduleStore } from '@/stores/scheduleStore';
import { Command, CornerDownLeft, Loader2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useTasksStore } from '@/stores/tasksStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { taskToUI, type TaskUI } from '@flexiplanner/types';
import { openNewActivityFlow } from '@/components/activities/NewActivityFlow';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

interface CommandSuggestion {
  type: 'action' | 'nlp';
  text: string;
  description?: string;
  action?: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [input, setInput] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const processingRef = useRef(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const setBlocks = useScheduleStore(s => s.setBlocks);
  const setTasks = useTasksStore(s => s.setTasks);

  const openNewFlow = (mode: 'choose' | 'personal' | 'team') => {
    onClose();
    window.setTimeout(() => openNewActivityFlow(mode), 0);
  };

  // Default commands
  const defaultCommands: CommandSuggestion[] = [
    {
      type: 'action',
      text: 'New activity',
      description: 'Choose personal or team activity',
      action: () => openNewFlow('choose'),
    },
    {
      type: 'action',
      text: 'Add activity',
      description: 'Create a personal activity',
      action: () => openNewFlow('personal'),
    },
    {
      type: 'action',
      text: 'Add team activity',
      description: 'Assign a shared team activity',
      action: () => openNewFlow('team'),
    },
    {
      type: 'action',
      text: 'Auto-schedule',
      description: 'Optimize unscheduled activities and movable routine blocks',
      action: async () => {
        setIsProcessing(true);
        try {
          const response = await api.schedule.autoSchedule();
          if (response.success) {
            const scheduled = response.data?.scheduled ?? 0;
            const optimized = response.data?.optimized ?? 0;
            const failed = response.data?.failed ?? 0;
            toast({
              title: failed ? 'Schedule partially solved' : 'Schedule optimized',
              description: `${scheduled} activities placed, ${optimized} routine blocks optimized, ${failed} left open.`,
              variant: scheduled === 0 && failed > 0 ? 'destructive' : 'default',
            });

            // Just use the API response to update local state,
            // since KV fetching immediately might return stale data.
            if (response.data && response.data.tasks) {
              import('@/stores/tasksStore').then(({ useTasksStore }) => {
                const updateTask = useTasksStore.getState().updateTask;
                response.data.tasks.forEach((t: any) => {
                  if (t.task) {
                    updateTask(t.id, taskToUI(t.task));
                  } else if (t.slot) {
                    updateTask(t.id, {
                      scheduledStart: t.slot.start,
                      scheduledEnd: t.slot.end,
                    });
                  }
                });
              });
            }

            // Also refresh ghost blocks if needed
            api.schedule.getGhostBlocks().then(blocksRes => {
              if (blocksRes.success && blocksRes.data) {
                setBlocks(normalizeScheduleBlocks(blocksRes.data));
              }
            });
          } else {
            toast({ title: 'Failed to auto-schedule', variant: 'destructive' });
          }
        } catch (err) {
          toast({ title: 'Error auto-scheduling', variant: 'destructive' });
        } finally {
          setIsProcessing(false);
          onClose();
        }
      },
    },
    {
      type: 'action',
      text: 'Week view',
      description: 'Switch to week view',
      action: () => {
        navigate('/week');
        onClose();
      },
    },
    {
      type: 'action',
      text: 'Day view',
      description: 'Switch to day view',
      action: () => {
        navigate('/day');
        onClose();
      },
    },
    {
      type: 'action',
      text: 'Month view',
      description: 'Switch to month view',
      action: () => {
        navigate('/month');
        onClose();
      },
    },
    {
      type: 'action',
      text: 'Kanban view',
      description: 'Switch to kanban view',
      action: () => {
        navigate('/kanban');
        onClose();
      },
    },
    {
      type: 'action',
      text: 'Table view',
      description: 'Switch to table view',
      action: () => {
        navigate('/table');
        onClose();
      },
    },
    {
      type: 'action',
      text: 'State',
      description: 'Log water, sleep, and current energy',
      action: () => {
        navigate('/state');
        onClose();
      },
    },
    {
      type: 'action',
      text: 'Analytics',
      description: 'View productivity stats',
      action: () => {
        navigate('/analytics');
        onClose();
      },
    },
  ];

  const [suggestions, setSuggestions] = useState<CommandSuggestion[]>(defaultCommands);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setInput('');
      setSuggestions(defaultCommands);
      setSelectedIndex(0);
      setIsProcessing(false);
      setStatusMessage('');
    }
  }, [open]);

  // Filter suggestions based on input
  useEffect(() => {
    setSelectedIndex(0);

    if (!input.trim()) {
      setSuggestions(defaultCommands);
      return;
    }

    const filtered = defaultCommands.filter(cmd =>
      cmd.text.toLowerCase().includes(input.toLowerCase())
    );

    // Add NLP suggestion for sentence-like commands, including punctuation from dictated text.
    if (input.trim().length > 3) {
      setSuggestions([
        {
          type: 'nlp',
          text: input,
          description: 'Execute scheduling command',
        },
        ...filtered,
      ]);
    } else {
      setSuggestions(filtered);
    }
  }, [input]);

  const handleSubmit = async (command?: CommandSuggestion) => {
    if (processingRef.current) return;
    const typedInput = input.trim();
    const selectedCommand = suggestions[selectedIndex];
    const cmd =
      command ||
      (typedInput && (!selectedCommand || selectedCommand.text !== typedInput)
        ? {
            type: 'nlp' as const,
            text: typedInput,
            description: 'Execute scheduling command',
          }
        : selectedCommand) ||
      (typedInput
        ? {
            type: 'nlp' as const,
            text: typedInput,
            description: 'Execute scheduling command',
          }
        : undefined);
    if (!cmd) return;

    processingRef.current = true;
    setIsProcessing(true);
    setStatusMessage(cmd.type === 'nlp' ? 'Interpreting your request...' : 'Running command...');

    try {
      if (cmd.type === 'nlp') {
        const stateHandled = await tryHandleStateCommand(cmd.text);
        if (stateHandled) {
          onClose();
          return;
        }

        // Send to NLP API
        const result = await api.nlp.command(cmd.text);
        setStatusMessage('Applying schedule changes...');

        if (result.success && result.data) {
          const notification = result.data.result?.notification;
          if (notification) {
            toast({
              title: notification.title,
              description: notification.message,
              variant:
                notification.type === 'error' || notification.type === 'infeasible'
                  ? 'destructive'
                  : 'default',
            });
          } else {
            toast({ title: 'Command processed successfully' });
          }

          applyOptimisticCommandResult(result.data);

          // If an activity was created or modified, refresh activities and blocks
          setStatusMessage('Refreshing activity board...');
          await Promise.all([
            api.tasks.list().then(taskRes => {
              if (taskRes.success && taskRes.data) {
                const tasksList = Array.isArray(taskRes.data)
                  ? taskRes.data
                  : (taskRes.data as any)?.tasks;
                if (tasksList) mergeRefreshedTasks(tasksList.map(taskToUI), setTasks);
              }
            }),
            api.schedule.getGhostBlocks().then(blockRes => {
              if (blockRes.success && blockRes.data) {
                setBlocks(normalizeScheduleBlocks(blockRes.data));
              }
            }),
          ]);
          setStatusMessage('Done.');
        } else {
          const message = result.error?.message || 'Unknown error';
          setStatusMessage(message);
          toast({
            title: 'Failed to process command',
            description: message,
            variant: 'destructive',
          });
          return;
        }
        window.setTimeout(onClose, 450);
      } else {
        // Handle built-in commands
        if (cmd.action) {
          cmd.action();
        } else {
          onClose();
        }
      }
    } catch (error) {
      console.error('Command execution error:', error);
      const message = error instanceof Error ? error.message : 'Command failed.';
      setStatusMessage(message);
      toast({ title: 'Failed to process command', description: message, variant: 'destructive' });
    } finally {
      processingRef.current = false;
      setIsProcessing(false);
    }
  };

  const tryHandleStateCommand = async (text: string): Promise<boolean> => {
    const water = parseWaterCommand(text);
    if (water) {
      setStatusMessage('Logging water...');
      const response = await api.state.logWater(water);
      if (!response.success) {
        toast({
          title: 'Water log failed',
          description: response.error?.message || 'Try the State page.',
          variant: 'destructive',
        });
        return true;
      }
      toast({ title: 'Water logged', description: `${water.amount} ${water.unit} added.` });
      window.dispatchEvent(new CustomEvent('flexiplanner-state-updated'));
      navigate('/state');
      return true;
    }

    const checkIn = parseCheckInCommand(text);
    if (checkIn) {
      setStatusMessage('Logging current state...');
      const response = await api.state.logCheckIn(checkIn);
      if (!response.success) {
        toast({
          title: 'State log failed',
          description: response.error?.message || 'Try the State page.',
          variant: 'destructive',
        });
        return true;
      }
      toast({ title: 'State logged', description: 'Current trajectory updated.' });
      window.dispatchEvent(new CustomEvent('flexiplanner-state-updated'));
      navigate('/state');
      return true;
    }

    return false;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="gap-0 p-0 sm:max-w-[600px]">
        <DialogTitle className="sr-only">Command Palette</DialogTitle>
        <DialogDescription className="sr-only">
          Search for commands and perform actions
        </DialogDescription>
        {/* Input */}
          <div className="flex items-center gap-3 border-b p-4">
          <Command className="text-muted-foreground h-5 w-5" />
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='Try "I need an hour now, free it up"'
            className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            autoFocus
            disabled={isProcessing}
          />
          {isProcessing && <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />}
        </div>

        {/* Suggestions */}
        <div className="max-h-[400px] overflow-y-auto">
          {suggestions.length === 0 ? (
            <div className="text-muted-foreground p-8 text-center">No commands found</div>
          ) : (
            <div className="p-2">
              {suggestions.map((suggestion, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSubmit(suggestion)}
                  className={cn(
                    'w-full rounded-md p-3 text-left transition-colors',
                    idx === selectedIndex ? 'bg-accent' : 'hover:bg-accent/50'
                  )}
                >
                  <div className="flex items-center gap-3">
                    {suggestion.type === 'nlp' && (
                      <Sparkles className="text-primary h-4 w-4 flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{suggestion.text}</div>
                      {suggestion.description && (
                        <div className="text-muted-foreground truncate text-sm">
                          {suggestion.description}
                        </div>
                      )}
                    </div>
                    {suggestion.type === 'nlp' && (
                      <Badge variant="secondary" className="flex-shrink-0">
                        AI
                      </Badge>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-muted-foreground flex flex-col items-stretch gap-2 border-t p-2 text-xs sm:flex-row sm:items-center sm:justify-between">
          <div className="hidden items-center gap-4 pl-2 sm:flex">
            <span>↑↓ Navigate</span>
            <span>↵ Execute</span>
            <span>Esc Close</span>
          </div>
          {statusMessage && (
            <div className="min-w-0 flex-1 truncate px-2 py-2 text-left text-xs text-muted-foreground sm:text-right">
              {statusMessage}
            </div>
          )}
          <Button
            type="button"
            size="sm"
            onClick={() => handleSubmit()}
            disabled={isProcessing || !input.trim()}
            className="h-9 shrink-0"
          >
            {isProcessing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CornerDownLeft className="h-4 w-4" />
            )}
            Execute
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const numberWords: Record<string, number> = {
  a: 1,
  an: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
};

function parseWaterCommand(text: string): null | { amount: number; unit: string; note: string } {
  const normalized = text.toLowerCase().replace(/\s+/g, ' ').trim();
  if (!/\b(water|drank|drink|hydrated|hydration)\b/.test(normalized)) return null;

  const match = normalized.match(
    /\b(\d+(?:\.\d+)?|a|an|one|two|three|four|five|six|seven|eight|nine|ten)\s*(glasses?|bottles?|oz|ounces?|ml|milliliters?)\b/
  );

  if (!match) return null;

  const amount = parseAmount(match[1]!);
  const unitText = match[2]!;
  const unit = unitText.startsWith('bottle')
    ? 'bottle'
    : unitText.startsWith('oz') || unitText.startsWith('ounce')
      ? 'oz'
      : unitText.startsWith('ml') || unitText.startsWith('milliliter')
        ? 'ml'
        : 'glass';

  return { amount, unit, note: text.trim() };
}

function parseCheckInCommand(text: string): null | Record<string, unknown> {
  const normalized = text.toLowerCase().replace(/\s+/g, ' ').trim();
  const looksLikeState =
    /\b(i feel|feeling|state check|check in|energy|emotion|body|resistance|rj|rejuvenation|productive)\b/.test(
      normalized
    );
  if (!looksLikeState) return null;

  const productivity = extractScore(normalized, /\b(?:p|productivity|productive)\s*(?:energy)?\s*(?:is|=|:)?\s*(\d{1,2})\b/);
  const rejuvenation = extractScore(normalized, /\b(?:rj|rejuvenation|relaxed)\s*(?:energy)?\s*(?:is|=|:)?\s*(\d{1,2})\b/);
  const resistance = extractScore(normalized, /\bresistance\s*(?:is|=|:)?\s*(\d{1,2})\b/);
  const hydration = extractScore(normalized, /\bhydration\s*(?:is|=|:)?\s*(\d{1,2})\b/);

  return {
    raw_text: text.trim(),
    productivity_energy: productivity,
    rejuvenation_energy: rejuvenation,
    resistance,
    hydration_feeling: hydration,
  };
}

function parseAmount(value: string): number {
  if (/^\d/.test(value)) return Number(value);
  return numberWords[value] || 1;
}

function applyOptimisticCommandResult(data: any) {
  const result = data?.result;
  const intent = data?.intent;
  if (!result?.success || !result.task_id) return;

  const store = useTasksStore.getState();
  const existing = store.getTask(result.task_id);
  const slot = result.new_slot;

  if (existing) {
    store.updateTask(result.task_id, {
      scheduledStart: slot?.start ?? existing.scheduledStart,
      scheduledEnd: slot?.end ?? existing.scheduledEnd,
    });
    return;
  }

  const title = inferCreatedTitle(intent);
  if (!title) return;

  const now = new Date().toISOString();
  const duration = inferCreatedDuration(intent);
  const workspace = useWorkspaceStore.getState();
  const activeBundles = workspace.bundles.filter(bundle => bundle.status === 'active');
  const bundleId = activeBundles.length === 1 ? activeBundles[0]!.id : null;
  const projectId = workspace.getBundle(bundleId)?.projectId || null;
  const optimisticTask: TaskUI = {
    id: result.task_id,
	    userId: '',
	    parentId: null,
	    bundleId,
	    projectId,
	    teamId: null,
	    assignedToUserId: null,
	    createdByUserId: null,
	    title,
    status: 'todo',
    priority: 3,
    depth: 0,
    position: store.tasks.length,
    estimatedDuration: duration,
    remainingDuration: duration,
    completionPercent: 0,
    deadline: null,
    energyLevel: undefined,
    stateProfile: intent?.state_profile,
    scheduledStart: slot?.start ?? null,
    scheduledEnd: slot?.end ?? null,
    completedAt: null,
    isRecurring: false,
    recurrenceRule: null,
    customAttributes: {},
    createdAt: now,
    updatedAt: now,
  };

  store.addTask(optimisticTask);
}

function mergeRefreshedTasks(refreshedTasks: TaskUI[], setTasks: (tasks: TaskUI[]) => void) {
  const merged = new Map(useTasksStore.getState().tasks.map(task => [task.id, task]));
  for (const task of refreshedTasks) {
    merged.set(task.id, task);
  }
  setTasks(Array.from(merged.values()));
}

function inferCreatedTitle(intent: any): string | null {
  if (!intent) return null;
  if (intent.action === 'create_task' || intent.action === 'create_and_schedule') {
    return intent.task_identifier || null;
  }
  const createOperation = Array.isArray(intent.operations)
    ? intent.operations.find((operation: any) => operation.type === 'create_task')
    : null;
  return createOperation?.title || createOperation?.task_identifier || null;
}

function inferCreatedDuration(intent: any): number | null {
  if (!intent) return null;
  if (Number.isFinite(intent.duration_minutes)) return intent.duration_minutes;
  const createOperation = Array.isArray(intent.operations)
    ? intent.operations.find((operation: any) => operation.type === 'create_task')
    : null;
  return Number.isFinite(createOperation?.duration_minutes) ? createOperation.duration_minutes : null;
}

function extractScore(text: string, pattern: RegExp): number | null {
  const match = text.match(pattern);
  if (!match?.[1]) return null;
  const score = Number(match[1]);
  if (!Number.isFinite(score)) return null;
  return Math.max(0, Math.min(10, score));
}

function normalizeScheduleBlocks(data: any) {
  const ghostBlocks = (data.ghost_blocks || []).map((block: any) => ({
    id: block.id,
    taskId: block.task_id,
    title: block.title || 'Suggested activity',
    startTime: block.proposed_start,
    endTime: block.proposed_end,
    isGhost: true,
  }));

  const workSegments = (data.work_segments || []).map((segment: any) => ({
    id: segment.id,
    taskId: segment.task_id,
    title: segment.segment_type === 'pause' ? 'Free time' : segment.title || 'Focused work',
    startTime: segment.start_time,
    endTime: segment.end_time,
    isGhost: false,
    priority: segment.segment_type === 'pause' ? 5 : 3,
    requiredEnergy: segment.segment_type === 'pause' ? 0 : undefined,
  }));

  return [...ghostBlocks, ...workSegments];
}
